import { Reducer } from 'redux';
import { decamelizeKeys } from 'humps';
import { CALL_API } from '../helpers/middleware/api';
import createRequestTypes from '../helpers/createRequestTypes'
import { OrganizationWithConsents, Consent } from 'types';

const FETCH_CONSENTS = createRequestTypes('FETCH_CONSENTS');
export const fetchConsents = (userId: number) => ({
  [CALL_API]: {
    types: FETCH_CONSENTS,
    endpoint: `/api/v2.0/users/${userId}/consents`,
  }
})

const PATCH_CONSENT = createRequestTypes('PATCH_CONSENT');
export const changeConsent = (value: boolean, userId: number, consentRequestId: number, purchaseId: number) => {
  if (value) {
    return {
      [CALL_API]: {
        types: PATCH_CONSENT,
        endpoint: `/api/v2.0/users/${userId}/consents/${consentRequestId}/_consent`,
        options: { method: 'POST' },
        additional: { consentRequestId }
      }
    }
  } else {
    return {
      [CALL_API]: {
        types: PATCH_CONSENT,
        endpoint: `/api/v2.0/users/${userId}/consents/${consentRequestId}/_revoke`,
        options: {
          method: 'POST',
          body: JSON.stringify(decamelizeKeys({ purchaseId }))
        },
        additional: { consentRequestId }
      }
    }
  }
}

export interface ConsentsState {
  fetching: boolean;
  fetched: boolean;
  patching: Set<number>;
  organizations: OrganizationWithConsents[];
  globalConsents: Consent[];
  error: boolean;
}

const initialState: ConsentsState = {
  fetching: false,
  fetched: false,
  patching: new Set(),
  organizations: [],
  globalConsents: [],
  error: false,
}

const reducer: Reducer<ConsentsState> = (state = initialState, action): ConsentsState => {
  switch(action.type) {
    case FETCH_CONSENTS.REQUEST:
      return { ...state, fetching: true, fetched: false };
    case FETCH_CONSENTS.SUCCESS: {
      const organizations = action.response.organizations.map((o: any): OrganizationWithConsents => ({
        name: o.organization.name,
        organizationId: o.organization.organization_id,
        consents: o.consent_requests.map((x: any): Consent => {
          if (x.consent) {
            return {
              consentRequest: {
                text: x.consent_request.consent_request_text,
                consentRequestId: x.consent_request.consent_request_id,
              },

              consent: {
                created: x.consent.created,
                expires: x.consent.expires,
                purchaseId: x.consent.purchase_id,
              }
            }
          } else {
            return {
              consentRequest: {
                text: x.consent_request.consent_request_text,
                consentRequestId: x.consent_request.consent_request_id,
              },
            }
          }
        })
      }))
      return { ...state, fetching: false, fetched: true, organizations };
    }
    case FETCH_CONSENTS.FAILURE:
      return { ...state, fetching: false, fetched: true, error: true };

    case PATCH_CONSENT.REQUEST: {
      return { ...state, patching: new Set(state.patching).add(action.consentRequestId) }
    }
    case PATCH_CONSENT.SUCCESS: {
      const patching = new Set(state.patching);
      patching.delete(action.consentRequestId);

      const orgIndex = state.organizations.findIndex(org => org.consents.findIndex(c => c.consentRequest.consentRequestId === action.consentRequestId) >= 0);
      const consentIndex = state.organizations[orgIndex].consents.findIndex(c => c.consentRequest.consentRequestId === action.consentRequestId);

      if (orgIndex >= 0 && consentIndex >= 0) {
        if (action.response.consent) {
          state.organizations[orgIndex].consents[consentIndex].consent = {
            created: action.response.consent.created,
            expires: action.response.consent.expires,
            purchaseId: action.response.consent.purchaseId,
          }
        } else {
          delete state.organizations[orgIndex].consents[consentIndex].consent;
        }
      }

      return { ...state, patching }
    }

    default:
      return state;
  }
}

export default reducer;
